import { serializeNode } from "@corca/latex";
import {
  Blockquote as ChatBlockquote,
  BulletList as ChatBulletList,
  BlockIndent,
  Code,
  Heading as ChatHeading,
  OrderedList as ChatOrderedList,
  Table as ChatTable,
  TableCell,
  TableCellBackgroundColor as ChatTableCellBackgroundColor,
  TableCellVerticalAlignment as ChatTableCellVerticalAlignment,
  TableHeader,
  TableRow,
  TaskItem,
  TaskList as ChatTaskList,
} from "@corca/tiptap";
import { Document } from "@tiptap/extension-document";
import { HardBreak } from "@tiptap/extension-hard-break";
import { HorizontalRule as ChatHorizontalRule } from "@tiptap/extension-horizontal-rule";
import { ListItem } from "@tiptap/extension-list";
import { Strike } from "@tiptap/extension-strike";
import { Subscript } from "@tiptap/extension-subscript";
import { Superscript } from "@tiptap/extension-superscript";
import { Text } from "@tiptap/extension-text";
import { Underline } from "@tiptap/extension-underline";
import { Dropcursor } from "@tiptap/extensions";

import { BlockNavigation } from "@/components/tiptap/block-navigation/BlockNavigation";
import { BlockAlign } from "@/components/tiptap/BlockAlign";
import { Blockquote } from "@/components/tiptap/Blockquote";
import { BlockStyle } from "@/components/tiptap/BlockStyle";
import { Bold } from "@/components/tiptap/Bold";
import { BulletList } from "@/components/tiptap/BulletList";
import { Callout } from "@/components/tiptap/Callout";
import { ChangeRequest, ChangeRequestPart } from "@/components/tiptap/ChangeRequest";
import { CodeBlock } from "@/components/tiptap/CodeBlock";
import { Collaboration } from "@/components/tiptap/Collaboration";
import { CollaborationCaretBroadcast } from "@/components/tiptap/CollaborationCaretBroadcast";
import { Collapse } from "@/components/tiptap/collapse/Collapse";
import { ContextMenu } from "@/components/tiptap/context-menu/ContextMenu";
import { CopyPaste } from "@/components/tiptap/copy-paste/CopyPaste";
import { CustomBlock } from "@/components/tiptap/CustomBlock";
import { DocumentTitle } from "@/components/tiptap/DocumentTitle";
import { DropImage } from "@/components/tiptap/DropImage";
import { EditorTranslator, type Translator } from "@/components/tiptap/EditorTranslator";
import { Embed } from "@/components/tiptap/Embed";
import { EmptyNode } from "@/components/tiptap/EmptyNode";
import { ExpressionSelection } from "@/components/tiptap/ExpressionSelection";
import { FileAttachmentPlugin } from "@/components/tiptap/FileAttachment";
import { Heading } from "@/components/tiptap/Heading";
import { HighlightSuggestedText } from "@/components/tiptap/HighlightSuggestedText";
import { HorizontalRule } from "@/components/tiptap/HorizontalRule";
import { ImagePlugin as ImageNode } from "@/components/tiptap/Image";
import { Italic } from "@/components/tiptap/Italic";
import { KeyboardHandler } from "@/components/tiptap/keyboard-handler/KeyboardHandler";
import { LatexInput } from "@/components/tiptap/LatexInput";
import { LineNumbers } from "@/components/tiptap/LineNumbers";
import { Link } from "@/components/tiptap/Link";
import { LockBlock } from "@/components/tiptap/lock/LockBlock";
import { LongPress } from "@/components/tiptap/long-press/LongPress";
import { Mention } from "@/components/tiptap/Mention";
import { OrderedList } from "@/components/tiptap/OrderedList";
import { Paragraph } from "@/components/tiptap/Paragraph";
import { SuggestPrompt } from "@/components/tiptap/SuggestPrompt";
import { Table } from "@/components/tiptap/tables/Table";
import { TableCellBackgroundColor } from "@/components/tiptap/tables/TableCellBackgroundColor";
import { TableCellVerticalAlignment } from "@/components/tiptap/tables/TableCellVerticalAlignment";
import { TailwindTextStyle } from "@/components/tiptap/TailwindTextStyle";
import { TaskList } from "@/components/tiptap/TaskList";
import { TiptapInlineExpression } from "@/components/tiptap/tiptap-expression/TiptapInlineExpression";
import { TrailingNode } from "@/components/tiptap/TrailingNode";
import { UploadPlaceholder } from "@/components/tiptap/UploadPlaceholder";
import { Placeholder } from "@/components/tiptap/utils/Placeholder";
import { VariableLink } from "@/components/tiptap/variable-link/VariableLink";
import { WhitespaceClick } from "@/components/tiptap/WhitespaceClick";

import { ClearFormatting } from "./ClearFormatting";
import { EditorPopover } from "./editor-popover/EditorPopover";
import { GraphPlugin } from "./Graph";
import { HighlightInsertedNodes } from "./HighlightInsertedNodes";
import { HighlightSelectionOnBlur } from "./HighlightSelectionOnBlur";
import { InlineMathIndentation } from "./InlineMathIndentation";
import { PhantomHint } from "./phantom-hint/PhantomHint";
import { PlotlyChartPlugin } from "./PlotlyChart";
import { TrackUserInput } from "./TrackUserInput";
import { TransformExpressionsToText } from "./TransformExpressionsToText";
import { WrapInGroup } from "./WrapInGroup";

import type { PhantomHintOptions } from "./phantom-hint/PhantomHint";
import type { CodeBlockOptions } from "@/components/tiptap/CodeBlock";
import type { DocumentTitleOptions } from "@/components/tiptap/DocumentTitle";
import type { DropImageOptions } from "@/components/tiptap/DropImage";
import type { CorcaDocumentLinkNavigator } from "@/components/tiptap/Link";
import type { MentionNavigator } from "@/components/tiptap/Mention";
import type { PlaceholderOptions } from "@/components/tiptap/utils/Placeholder";
import type { UploadScope } from "@/components/tiptap/utils/uploadScope";
import type { ChatMessageModel } from "@/models/chat/ChatMessageModel";
import type { ChatModel } from "@/models/chat/ChatModel";
import type { DocumentModel } from "@/models/DocumentModel";
import type { Extendable } from "@tiptap/core";

/**
 * common extensions used in chat tiptap and other basic editors
 */
export function corcaKitExtensions({
  doc,
  chat,
  message,
  uploadScope,
  alwaysEager,
  mathIndentation = true,
  onOpenCorcaDocumentLink,
  onOpenMention,
  getAuthorId,
}: {
  doc: DocumentModel;
  chat?: ChatModel;
  message?: ChatMessageModel;
  uploadScope?: UploadScope;
  /** Disables lazy expression rendering. */
  alwaysEager?: boolean;
  mathIndentation?: boolean;
  onOpenCorcaDocumentLink?: CorcaDocumentLinkNavigator;
  onOpenMention?: MentionNavigator;
  getAuthorId?: () => string | undefined;
}): Extendable[] {
  return [
    Text,
    HardBreak,
    TailwindTextStyle,
    Underline,
    Subscript,
    Superscript,
    Strike,
    Bold,
    Italic,
    Dropcursor,
    Code,
    Link.configure({ doc, onOpenCorcaDocumentLink }),
    VariableLink.configure({ doc }),
    WhitespaceClick.configure({ doc }),
    CopyPaste.configure({ doc, uploadScope }),
    SuggestPrompt.configure({ doc }),
    ContextMenu.configure({ doc }),
    LongPress.configure({ doc }),
    TiptapInlineExpression.configure({
      doc,
      getNodeJson: (id: string) => doc.nodes.get(id)?.toJSON(),
      serializeNode,
      alwaysEager,
    }),
    Mention.configure({ doc, chat, message, onOpenMention, getAuthorId }),
    KeyboardHandler.configure({ doc }),
    HighlightSuggestedText.configure({ doc }),
    HighlightInsertedNodes,
    ...(mathIndentation ? [InlineMathIndentation.configure({ doc })] : []),
    Collaboration.configure({
      doc,
      document: doc.yDoc,
      field: doc.textNode.id,
    }),
    TrailingNode.configure({ onlyAfter: ["table", "image", "embed", "fileAttachment"] }),
    ExpressionSelection.configure({ doc }),
    WrapInGroup.configure({ doc }),
    LatexInput.configure({ doc }),
  ];
}

/**
 * extensions for the main editor.
 *
 * Leave it outside of React if possible so that we could use it for building PM schema if required.
 */
export function editorExtensions(
  options: { doc: DocumentModel; translator?: Translator | null } & Partial<
    DocumentTitleOptions & PlaceholderOptions & CodeBlockOptions & PhantomHintOptions
  >,
): Extendable[] {
  const {
    doc,
    defaultPageTitle,
    onTitleChange,
    auth,
    placeholder,
    rootStore,
    translator = null,
  } = options;

  return [
    DocumentTitle.configure({ doc, defaultPageTitle, onTitleChange }),
    Paragraph,
    HighlightSelectionOnBlur,
    DropImage.configure({ doc }),
    UploadPlaceholder,
    ImageNode.configure({ doc }),
    GraphPlugin.configure({ doc }),
    PlotlyChartPlugin.configure({ doc }),
    Embed.configure({ doc }),
    FileAttachmentPlugin.configure({ doc }),
    BlockAlign,
    BlockIndent,
    BlockStyle,
    Collapse,
    BlockNavigation.configure({ doc }),
    LockBlock.configure({ doc }),
    EditorTranslator.configure({ translator }),
    ClearFormatting,
    EditorPopover,
    Heading,
    BulletList,
    OrderedList,
    TaskList,
    Blockquote,
    Callout,
    ListItem,
    TaskItem,
    HorizontalRule.configure({ doc }),
    CodeBlock.configure({
      doc,
      auth,
    }),
    EmptyNode,
    ChangeRequestPart,
    ChangeRequest,
    Table.configure({ doc, allowTableNodeSelection: true }),
    TableRow,
    TableHeader,
    TableCell,
    TableCellBackgroundColor,
    TableCellVerticalAlignment,
    Placeholder.configure({
      placeholder,
    }),
    LineNumbers.configure({ doc }),
    TransformExpressionsToText.configure({ doc }),
    TrackUserInput.configure({ doc }),
    // Broadcasts the local cursor via the upstream collaboration-caret plugin, but renders
    // nothing — remote presence is drawn by the AwarenessCursors overlay. A decoration over
    // a math node (selection or caret widget) re-parents its node-view DOM and blurs the
    // in-math caret of whoever is editing it; an overlay never touches the editor DOM.
    doc.awareness &&
      CollaborationCaretBroadcast.configure({
        provider: doc.awareness.provider,
        user: doc.awareness.user,
      }),
    PhantomHint.configure({
      doc,
      rootStore,
    }),
  ].filter(Boolean);
}

export function chatExtensions(
  options: {
    doc: DocumentModel;
  } & Partial<DocumentTitleOptions & PlaceholderOptions & CodeBlockOptions & DropImageOptions>,
): Extendable[] {
  const { doc, renderPlace, uploadScope, placeholder } = options;

  return [
    Document,
    Paragraph.configure({ doc, renderPlace }),
    ChatHeading,
    ChatBlockquote,
    ChatBulletList,
    ChatOrderedList,
    ChatTaskList,
    ListItem,
    TaskItem,
    ImageNode.configure({
      doc,
      disableOcr: renderPlace === "chat-message",
      disableResize: true,
      className: "max-w-full",
      renderPlace,
      uploadScope,
    }),
    CodeBlock.configure({ doc, renderPlace }),
    ChatTable,
    TableRow,
    TableHeader,
    TableCell,
    ChatTableCellBackgroundColor,
    ChatTableCellVerticalAlignment,
    ChatHorizontalRule.configure({
      HTMLAttributes: { class: "w-full border-gray-400 dark:border-gray-600 h-1.5" },
    }),
    Placeholder.configure({
      placeholder,
    }),
    CustomBlock,
  ];
}
